import PrefixPath from "../../config/AppConfig";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license";
import { PlusCircleOutlined, MinusCircleOutlined } from "@ant-design/icons";
import LoadingSkeletonOverlay from "./LoadingSkeletonOverlay";
import "./MuiTable.scss";

LicenseInfo.setLicenseKey(PrefixPath.MUI_License_KEY);

const CustomExpandIcon = () => <PlusCircleOutlined className="sub-table-icons" />;
const CustomCollapseIcon = () => <MinusCircleOutlined className="sub-table-icons" />;

const MuiTable = ({
  apiRef,
  onRowClick,
  rows,
  columnOrder,
  getRowId,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  getDetailPanelContent,
  detailPanelExpandedRowIds,
  isRowExpandable,
  onDetailPanelExpandedRowIdsChange,
  initialState,
  slots,
  sx,
  getRowClassName,
  disableRowSelectionOnClick,
  // Server-side (opt-in) — defaults keep the current client-side behavior
  paginationMode,
  sortingMode,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  pageSizeOptions = [10, 20, 50, 100],
  loading,
  getRowHeight,
}) => {
  const serverProps = Object.fromEntries(
    Object.entries({
      paginationMode,
      sortingMode,
      rowCount,
      paginationModel,
      onPaginationModelChange,
      sortModel,
      onSortModelChange,
    }).filter(([, value]) => value !== undefined)
  );

  return (
    <div className="row-cursor-pointer custom-table">
      <DataGridPro
        apiRef={apiRef}
        getRowId={getRowId ?? ((row) => row?.id)}
        onRowClick={onRowClick}
        rows={rows ?? []}
        columns={columnOrder}
        showToolbar
        loading={loading}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            showColumnsButton: true,
            showFilterButton: true,
            showDensitySelector: true,
            showExport: false,
          },
          // Skeleton rows while `loading` is true (both when refreshing existing
          // rows and on the very first load), instead of the progress bar.
          loadingOverlay: {
            variant: "skeleton",
            noRowsVariant: "skeleton",
          },
        }}
        slots={{
          detailPanelExpandIcon: CustomExpandIcon,
          detailPanelCollapseIcon: CustomCollapseIcon,
          loadingOverlay: LoadingSkeletonOverlay,
          ...slots,
        }}
        autoHeight
        pagination
        {...serverProps}
        pageSizeOptions={pageSizeOptions}
        initialState={{
          // Only set the initial pageSize when pagination is NOT controlled
          ...(paginationModel
            ? {}
            : { pagination: { paginationModel: { pageSize: 10 } } }),
          ...initialState,
        }}
        checkboxSelection={false}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        experimentalFeatures={{ columnGrouping: true }}
        getRowHeight={getRowHeight}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => "auto"}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        isRowExpandable={isRowExpandable}
        onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
        getRowClassName={getRowClassName}
        sx={[{
          border: "none",
          fontFamily: "inherit",
          backgroundColor: "#fff",

          // Give the overlays (no-rows/loading) a height with autoHeight,
          // otherwise the empty-state content collapses and is not visible.
          "--DataGrid-overlayHeight": "260px",

          // Loading skeleton: let the overlay box hug the 5 fixed skeleton
          // rows exactly (no clipped last row, no leftover gap). Scoped to the
          // skeleton via :has so the no-rows overlay keeps its 260px height.
          "& .MuiDataGrid-overlayWrapperInner:has(.rg-skeleton)": {
            height: "auto !important",
          },

          // ── Column separators ──────────────────────────────────────
          "& .MuiDataGrid-columnSeparator": {
            visibility: "hidden",
          },

          // ── Headers ───────────────────────────────────────────────
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F4F4F4",
            borderBottom: "1px solid #e0e0e0",
            minHeight: "44px !important",
            maxHeight: "44px !important",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "0 16px",
            height: "44px !important",
            backgroundColor: "#F4F4F4",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "14px",
            color: "rgba(0,0,0,0.88)",
            letterSpacing: "0.01em",
          },
          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-sortIcon": {
            color: "#9ca3af",
          },
          "& .MuiDataGrid-menuIconButton": {
            opacity: 0,
          },
          "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
            opacity: 1,
          },

          // ── Rows ──────────────────────────────────────────────────
          "& .MuiDataGrid-row": {
            borderBottom: "1px solid #f3f4f6",
            minHeight: "52px !important",
            maxHeight: "none !important",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f9fafb",
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "#eff6ff",
          },
          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "#dbeafe",
          },

          // ── Cells ─────────────────────────────────────────────────
          "& .MuiDataGrid-cell": {
            border: "none",
            padding: "0 16px",
            fontSize: "14px",
            color: "#111827",
            display: "flex",
            alignItems: "center",
            minHeight: "52px !important",
            maxHeight: "none !important",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },

          // ── Detail panel expand cell ───────────────────────────────
          "& .MuiDataGrid-detailPanelToggleCell": {
            padding: "0 8px",
          },

          // ── Filler / virtual scroller ──────────────────────────────
          "& .MuiDataGrid-filler": {
            backgroundColor: "#fff",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "#fff",
          },

          // ── Footer / pagination ────────────────────────────────────
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#fff",
            minHeight: "48px",
          },

          // ── Toolbar ───────────────────────────────────────────────
          "& .MuiDataGrid-toolbarContainer": {
            padding: "8px 12px",
            borderBottom: "1px solid #f0f0f0",
            backgroundColor: "#fff",
            gap: "8px",
          },

          // ── Disabled buttons ──────────────────────────────────────
          "&& .MuiButtonBase-root.MuiIconButton-root.Mui-disabled": {
            backgroundColor: "transparent !important",
            color: "rgba(0,0,0,0.26) !important",
            cursor: "not-allowed",
            pointerEvents: "auto",
            border: "none !important",
          },
          "&& .MuiButtonBase-root.MuiIconButton-root.Mui-disabled svg": {
            cursor: "not-allowed",
            border: "none !important",
            backgroundColor: "transparent !important",
            color: "rgba(0,0,0,0.26) !important",
          },
        }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      />
    </div>
  );
};

export default MuiTable;
